import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { TRIWULAN_OPTIONS } from "../lib/constants";
import { copyTriwulan } from "../lib/api";

export const CopyTriwulanModal = ({ open, jobs, onClose, onChanged }) => {
  const [source, setSource] = useState("");
  const [targetTw, setTargetTw] = useState("");
  const [targetYear, setTargetYear] = useState("");
  const [resetStatus, setResetStatus] = useState(true);
  const [clearPhotos, setClearPhotos] = useState(true);
  const [saving, setSaving] = useState(false);

  const sources = useMemo(() => {
    const map = new Map();
    for (const j of jobs) {
      if (!j.triwulan) continue;
      const key = `${j.triwulan}|${j.tahun || ""}`;
      if (!map.has(key)) map.set(key, { key, triwulan: j.triwulan, tahun: j.tahun || null, count: 0 });
      map.get(key).count += 1;
    }
    return Array.from(map.values()).sort((a, b) => (b.tahun || 0) - (a.tahun || 0) || a.triwulan - b.triwulan);
  }, [jobs]);

  useEffect(() => {
    if (!open) return;
    setSource(sources[0]?.key || "");
    setTargetTw("");
    setTargetYear(String(new Date().getFullYear()));
    setResetStatus(true);
    setClearPhotos(true);
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const src = sources.find((s) => s.key === source);

  const submit = async () => {
    if (!src) return toast.error("Pilih triwulan sumber");
    if (!targetTw) return toast.error("Pilih triwulan tujuan");
    if (src.triwulan === Number(targetTw) && String(src.tahun || "") === String(targetYear || "")) {
      return toast.error("Triwulan tujuan harus berbeda dari sumber");
    }
    setSaving(true);
    try {
      const res = await copyTriwulan({
        source_triwulan: src.triwulan,
        source_tahun: src.tahun,
        target_triwulan: Number(targetTw),
        target_tahun: targetYear ? Number(targetYear) : null,
        reset_status: resetStatus,
        clear_photos: clearPhotos,
      });
      toast.success(`${res.created} pekerjaan disalin ke triwulan baru`);
      onChanged && onChanged();
      onClose();
    } catch {
      toast.error("Gagal menyalin");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 outline-none bg-white font-semibold";

  return (
    <div className="no-print fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-auto flex flex-col max-h-[92vh] animate-fade-up" data-testid="copy-triwulan-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-teal-800 text-white flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-bold flex items-center gap-2"><i className="ph-bold ph-copy-simple text-teal-300" /> Salin Triwulan</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10" data-testid="copy-triwulan-close"><i className="ph-bold ph-x text-xl" /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          <p className="text-xs text-slate-500">Menyalin seluruh daftar pekerjaan dari satu triwulan sebagai dasar triwulan baru.</p>

          {sources.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-xs border border-slate-200 rounded-xl">Belum ada pekerjaan bertriwulan untuk disalin.</div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Salin dari (sumber) <span className="text-red-500">*</span></label>
                <select value={source} onChange={(e) => setSource(e.target.value)} className={`${inputCls} w-full`} data-testid="copy-source-select">
                  {sources.map((s) => (
                    <option key={s.key} value={s.key}>Triwulan {s.triwulan}{s.tahun ? ` - ${s.tahun}` : ""} ({s.count} pekerjaan)</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-center text-slate-300"><i className="ph-bold ph-arrow-down text-xl" /></div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Ke Triwulan (tujuan) <span className="text-red-500">*</span></label>
                  <select value={targetTw} onChange={(e) => setTargetTw(e.target.value)} className={`${inputCls} w-full`} data-testid="copy-target-select">
                    <option value="">— Pilih —</option>
                    {TRIWULAN_OPTIONS.map((t) => <option key={t} value={t}>Triwulan {t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Tahun tujuan</label>
                  <input type="number" value={targetYear} onChange={(e) => setTargetYear(e.target.value)} placeholder="2026" min="2000" max="2100" className={`${inputCls} w-full`} data-testid="copy-target-year" />
                </div>
              </div>

              <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={resetStatus} onChange={(e) => setResetStatus(e.target.checked)} className="w-4 h-4 accent-teal-600" data-testid="copy-reset-status" />
                  Setel ulang status jadi "Belum Dikerjakan"
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={clearPhotos} onChange={(e) => setClearPhotos(e.target.checked)} className="w-4 h-4 accent-teal-600" data-testid="copy-clear-photos" />
                  Kosongkan foto bukti (mulai bersih)
                </label>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-50">Batal</button>
          <button onClick={submit} disabled={saving || sources.length === 0} className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg shadow-sm disabled:opacity-60" data-testid="copy-triwulan-submit">
            {saving ? "Menyalin..." : "Salin Pekerjaan"}
          </button>
        </div>
      </div>
    </div>
  );
};
